import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import {
  ArrowForward,
  ConfirmationNumber,
  CreditCard,
  Info,
} from "@mui/icons-material"
import InputApp from "../../../app/components/input-app"
import { RootState } from "../../../app/store"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { sendRaffleHeadFormThunk, updateinputRaffleAction } from "../handle-raffle.slice"
import { useParams } from "react-router-dom"
import { numberToCurrency } from "../../../utils/numbers.utils"
import LoadingIndicator from "../../../app/components/loading-indicator"
import { PaymentProcessorEnum } from "../../../app/models/payment-processor.enum"

export default function RaffleHeadForm() {
  const { raffleId } = useParams()
  const { form, currentRaffle, showLoading } = useAppSelector((state: RootState) => state.raffle)
  const dispatch = useAppDispatch()
  const isEdit = Boolean(raffleId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    dispatch(sendRaffleHeadFormThunk({ raffleHeadForm: form, raffleId }))
  }

  const changeInput = (val: string, name: string) => {
    dispatch(updateinputRaffleAction({ name, value: val }))
  }

  return (
    <>
      <LoadingIndicator open={showLoading} />
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Info color="primary" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Información general
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <InputApp
                  name="name"
                  onChange={changeInput}
                  placeholder="Nombre del sorteo"
                  value={form.name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputApp
                  name="code"
                  onChange={changeInput}
                  placeholder="Código URL"
                  value={form.code}
                  required
                  inputProps={{ style: { textTransform: "lowercase" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <InputApp
                  name="description"
                  onChange={changeInput}
                  placeholder="Descripción"
                  value={form.description}
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <ConfirmationNumber color="primary" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Configuración de tickets
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <InputApp
                  name="ticketPrice"
                  onChange={changeInput}
                  placeholder="Precio ticket"
                  value={form.ticketPrice}
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputApp
                  name="nTickets"
                  onChange={changeInput}
                  placeholder="Mín. por compra"
                  value={form.nTickets}
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputApp
                  name="numberLength"
                  onChange={changeInput}
                  placeholder="Dígitos número"
                  value={form.numberLength}
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputApp
                  name="nTicketPrizeSure"
                  onChange={changeInput}
                  placeholder="Ticket premio menor"
                  value={form.nTicketPrizeSure}
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputApp
                  name="datePrize"
                  onChange={changeInput}
                  placeholder="Fecha del sorteo"
                  value={form.datePrize}
                  type="date"
                  required
                />
              </Grid>
              {currentRaffle !== undefined && (
                <Grid item xs={12} sm={6} md={4}>
                  <InputApp
                    placeholder="Costo total premios"
                    value={`$ ${numberToCurrency(currentRaffle.cost)}`}
                    readonly
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <CreditCard color="primary" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Procesador de pago
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="payment-processor-label">Procesador</InputLabel>
                  <Select
                    labelId="payment-processor-label"
                    label="Procesador"
                    value={form.paymentProcessor ?? PaymentProcessorEnum.wompi}
                    onChange={(e) => {
                      const processor = e.target.value as PaymentProcessorEnum
                      dispatch(updateinputRaffleAction({ name: "paymentProcessor", value: processor }))
                      if (processor === PaymentProcessorEnum.wompi) {
                        dispatch(updateinputRaffleAction({ name: "paymentCurrency", value: "COP" }))
                      } else if (form.paymentCurrency === "COP") {
                        dispatch(updateinputRaffleAction({ name: "paymentCurrency", value: "USD" }))
                      }
                    }}
                  >
                    <MenuItem value={PaymentProcessorEnum.wompi}>
                      Wompi — Tarjeta (Colombia)
                    </MenuItem>
                    <MenuItem value={PaymentProcessorEnum.blockbee}>
                      BlockBee — Criptomonedas
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="payment-currency-label">Moneda</InputLabel>
                  <Select
                    labelId="payment-currency-label"
                    label="Moneda"
                    value={form.paymentCurrency ?? "COP"}
                    onChange={(e) =>
                      dispatch(updateinputRaffleAction({ name: "paymentCurrency", value: e.target.value }))
                    }
                  >
                    {(form.paymentProcessor === PaymentProcessorEnum.blockbee
                      ? ["USD", "EUR", "COP", "GBP"]
                      : ["COP"]
                    ).map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              endIcon={<ArrowForward />}
              sx={{ cursor: "pointer", py: 1.25 }}
            >
              {isEdit ? "Guardar y continuar" : "Crear sorteo y continuar"}
            </Button>
            {!isEdit && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, textAlign: "center" }}>
                Al crear el sorteo podrás subir imágenes y configurar premios en los siguientes pasos.
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    </>
  )
}
